import type { PlantDetail, PlantPhoto } from "../../shared/types";
import { addPlantPhoto, deletePlantPhoto, fetchPlant, setPlantCoverPhoto } from "../api";

function formatShortDate(ms: number) {
  try {
    return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" }).format(new Date(ms));
  } catch {
    return "—";
  }
}

export function PlantPhotosPanel(props: {
  plantId: string;
  photos: PlantPhoto[];
  heroPhotoId: string | null;
  photoBusy: boolean;
  photoActionBusy: string | null;
  onHeroPhotoId: (id: string) => void;
  onPhotosChange: (plant: PlantDetail) => void;
  onPhotoBusy: (busy: boolean) => void;
  onPhotoActionBusy: (id: string | null) => void;
  onRefreshHome: () => Promise<void>;
}) {
  const {
    plantId,
    photos,
    heroPhotoId,
    photoBusy,
    photoActionBusy,
    onHeroPhotoId,
    onPhotosChange,
    onPhotoBusy,
    onPhotoActionBusy,
    onRefreshHome,
  } = props;

  return (
    <div className="panel">
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "baseline" }}>
        <h2 style={{ margin: 0, fontSize: "1.05rem" }}>Photos</h2>
        <span className="muted" style={{ fontSize: "0.85rem" }}>
          {photos.length} {photos.length === 1 ? "photo" : "photos"}
        </span>
      </div>
      <p className="muted" style={{ marginTop: 8, marginBottom: 10 }}>
        Add more photos over time to track growth. Tap a thumbnail to preview; set one as the cover for the home grid.
      </p>
      {photos.length > 0 ? (
        <div className="photo-gallery" role="list">
          {photos.map((ph) => (
            <div key={ph.id} className="photo-thumb-wrap" role="listitem">
              <button
                type="button"
                className={`photo-thumb${ph.id === heroPhotoId ? " photo-thumb--active" : ""}`}
                onClick={() => onHeroPhotoId(ph.id)}
                aria-label={`Preview photo from ${formatShortDate(ph.created_at)}`}
              >
                <img src={ph.url} alt="" loading="lazy" decoding="async" />
                {ph.is_cover ? <span className="photo-thumb__badge">Cover</span> : null}
              </button>
              <div className="photo-thumb__actions">
                {!ph.is_cover ? (
                  <button
                    type="button"
                    className="btn btn-ghost"
                    style={{ padding: "4px 8px", fontSize: "0.75rem" }}
                    disabled={photoActionBusy === ph.id}
                    onClick={async () => {
                      onPhotoActionBusy(ph.id);
                      try {
                        await setPlantCoverPhoto(plantId, ph.id);
                        const { plant: p } = await fetchPlant(plantId);
                        onPhotosChange(p);
                        onHeroPhotoId(ph.id);
                        await onRefreshHome();
                      } catch (err) {
                        alert(err instanceof Error ? err.message : "Failed");
                      } finally {
                        onPhotoActionBusy(null);
                      }
                    }}
                  >
                    Cover
                  </button>
                ) : null}
                {photos.length > 1 ? (
                  <button
                    type="button"
                    className="btn btn-ghost btn-danger"
                    style={{ padding: "4px 8px", fontSize: "0.75rem" }}
                    disabled={photoActionBusy === `del-${ph.id}`}
                    onClick={async () => {
                      if (!confirm("Delete this photo?")) return;
                      onPhotoActionBusy(`del-${ph.id}`);
                      try {
                        await deletePlantPhoto(plantId, ph.id);
                        const { plant: p } = await fetchPlant(plantId);
                        onPhotosChange(p);
                        const cover = p.photos.find((x) => x.is_cover) ?? p.photos[0];
                        if (heroPhotoId === ph.id && cover) onHeroPhotoId(cover.id);
                        await onRefreshHome();
                      } catch (err) {
                        alert(err instanceof Error ? err.message : "Failed");
                      } finally {
                        onPhotoActionBusy(null);
                      }
                    }}
                  >
                    Del
                  </button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      ) : null}
      <div className="row" style={{ marginTop: 12 }}>
        <label className="btn btn-ghost" style={{ cursor: photoBusy ? "wait" : "pointer" }}>
          {photoBusy ? "Uploading…" : "Add photo"}
          <input
            type="file"
            accept="image/*"
            capture="environment"
            className="sr-only"
            disabled={photoBusy}
            onChange={async (e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              if (!file) return;
              onPhotoBusy(true);
              try {
                const fd = new FormData();
                fd.set("photo", file);
                await addPlantPhoto(plantId, fd);
                const { plant: p } = await fetchPlant(plantId);
                onPhotosChange(p);
                const newest = p.photos[0];
                if (newest) onHeroPhotoId(newest.id);
              } catch (err) {
                alert(err instanceof Error ? err.message : "Upload failed");
              } finally {
                onPhotoBusy(false);
              }
            }}
          />
        </label>
      </div>
    </div>
  );
}
